/**
 * Single source of truth for client-side image-upload validation (MIME + size).
 *
 * Defense-in-depth / UX gate only — NOT a security boundary. The authoritative
 * check is the backend's shared validator (own-storage assertion + content-type
 * + size); this just rejects obviously-wrong files before the upload round-trip
 * so the user gets immediate feedback instead of a silent server reject.
 * (Added per Samer review on PR #404 — client guard across every image flow.)
 *
 * Used by `UploadImageDialog` (staff / practitioner / practice / license photos)
 * and the org-logo field in `account-details-page.tsx`, so the rule lives in one
 * place rather than drifting per call site.
 */
export const DEFAULT_IMAGE_ACCEPT = 'image/jpeg,image/png,image/heic';
export const DEFAULT_IMAGE_MAX_SIZE_MB = 10;

export interface ValidateImageFileOptions {
  /** Comma-separated MIME list. Supports wildcards like `image/*`. */
  accept?: string;
  /** Max size in megabytes. */
  maxSizeMB?: number;
}

/**
 * Returns a human-readable error string when the file fails the MIME allowlist
 * or the size cap, or `null` when it's acceptable. Wildcard entries
 * (`image/*`) match on the `type/` prefix; everything else is an exact match.
 */
export function validateImageFile(
  file: File,
  {
    accept = DEFAULT_IMAGE_ACCEPT,
    maxSizeMB = DEFAULT_IMAGE_MAX_SIZE_MB,
  }: ValidateImageFileOptions = {},
): string | null {
  const acceptedTypes = accept
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  if (acceptedTypes.length > 0) {
    // MIME types are case-insensitive (RFC 2045 §5.1) — normalize both
    // sides so an uppercased `IMAGE/JPEG` still matches. PROD-1095.
    const fileType = file.type.toLowerCase();
    const matches = acceptedTypes.some((raw) => {
      const t = raw.toLowerCase();
      return t.endsWith('/*') ? fileType.startsWith(t.slice(0, -1)) : t === fileType;
    });
    if (!matches) {
      return `Unsupported file type. Allowed: ${acceptedTypes.join(', ')}`;
    }
  }

  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB > maxSizeMB) {
    return `File is ${sizeMB.toFixed(1)} MB — max ${maxSizeMB} MB`;
  }

  return null;
}
